import { useTranslations } from 'next-intl';

import { bitcoin as IconBitcoin } from '@/components/icons/bitcoin';
import { triangleDown as IconTriangleDown } from '@/components/icons/triangleDown';
import { LoadingBox } from '@/components/spinner';
import { useNetwork } from '@/stores/useAppStore';
import { cn } from '@/utils/cn';
import { formatUsdValue } from '@/utils/formatUsdValue';

import { colorByNetwork } from '../settings/NetworkSettings/data';

interface BannerContentProps {
  price: number | undefined;
  isLoadingPrice: boolean;
  change: number | undefined;
  isLoading: boolean;
  isDown: boolean;
  interval: string;
  onIntervalClick: VoidFunction;
}

const BannerContent: React.FC<BannerContentProps> = ({
  price,
  isLoadingPrice,
  change,
  isLoading,
  isDown,
  interval,
  onIntervalClick,
}: BannerContentProps) => {
  const t = useTranslations('home.status');
  const selectedNetwork = useNetwork();

  return (
    <div
      id="BitcoinBanner"
      className="hidden 1320:flex h-11 items-center gap-3 rounded-full bg-surface-secondary px-3 py-2 text-text-primary"
    >
      <IconBitcoin color={colorByNetwork(selectedNetwork)} />
      <div className="hidden xl:flex flex-row gap-1 whitespace-nowrap">
        Bitcoin
        <div className="opacity-50">BTC</div>
      </div>
      <div aria-hidden="true" className="h-6 w-[2px] bg-divider 1320:mx-1" />
      {isLoadingPrice ? (
        <LoadingBox width={60} height={24} className="before:via-text-primary/30" />
      ) : (
        <div className="whitespace-nowrap">{price === undefined ? t('notAvailable') : formatUsdValue(price)}</div>
      )}
      {isLoading ? (
        <LoadingBox width={100} height={24} className="hidden xl:block before:via-text-primary/30" />
      ) : (
        <button
          type="button"
          className={cn(
            'hidden 1320:flex flex-row select-none items-center gap-1 cursor-pointer group bg-transparent border-0 p-0 focus:outline-hidden',
            {
              'text-text-secondary': isLoading,
              'text-red-100': isDown && !isLoading,
              'text-green-100': !isDown && !isLoading,
            },
          )}
          onClick={onIntervalClick}
        >
          <div className={cn({ 'rotate-180': !isDown, 'animate-pulse': isLoading })}>{change !== undefined && <IconTriangleDown />}</div>
          <div className={cn({ 'animate-pulse': isLoading })}>
            {change === undefined ? t('notAvailable') : `${change.toString().replace('-', '')}%`}
          </div>
          <div className="uppercase group-hover:opacity-75">({interval})</div>
        </button>
      )}
    </div>
  );
};

export default BannerContent;
